/**
 * useChildProgress Hook
 *
 * Fetches all progress data for a child in parallel for the guardian view.
 * Includes SAT scores, practice plan, recommendations, score history, and activity heatmap.
 */

import { useState, useEffect, useCallback } from 'react'
import api from '../services/api'

import { createLogger } from '../utils/logger'

const logger = createLogger('useChildProgress')

/**
 * Hook to fetch comprehensive progress data for a guardian's child
 * @param {string} childId - The child's user ID
 * @returns {Object} Progress data, loading state, error, and refresh function
 */
export function useChildProgress(childId) {
  const [data, setData] = useState({
    perSubjectScores: null,
    practiceRecommendation: null,
    recommendations: null,
    mathActualHistory: null,
    rwActualHistory: null,
    activityHeatmap: null,
    goal: null
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    if (!childId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Fetch all data in parallel
      const [
        perSubjectResponse,
        practiceResponse,
        recommendationsResponse,
        mathHistoryResponse,
        rwHistoryResponse,
        activityResponse,
        goalResponse
      ] = await Promise.allSettled([
        api.get(`/predictions/user/${childId}/current`),
        api.get(`/predictions/user/${childId}/practice-recommendation`),
        api.get(`/predictions/user/${childId}/recommendations`),
        api.get(`/predictions/user/${childId}/actual-history/SAT_MATH`),
        api.get(`/predictions/user/${childId}/actual-history/SAT_READING_WRITING`),
        api.get(`/user/${childId}/activity-heatmap`),
        api.get(`/predictions/user/${childId}/goal`)
      ])

      setData({
        perSubjectScores:
          perSubjectResponse.status === 'fulfilled' ? perSubjectResponse.value.data.data : null,
        practiceRecommendation:
          practiceResponse.status === 'fulfilled' ? practiceResponse.value.data.data : null,
        recommendations:
          recommendationsResponse.status === 'fulfilled'
            ? recommendationsResponse.value.data.data
            : null,
        mathActualHistory:
          mathHistoryResponse.status === 'fulfilled' ? mathHistoryResponse.value.data.data : null,
        rwActualHistory:
          rwHistoryResponse.status === 'fulfilled' ? rwHistoryResponse.value.data.data : null,
        activityHeatmap:
          activityResponse.status === 'fulfilled' ? activityResponse.value.data.data : null,
        goal: goalResponse.status === 'fulfilled' ? goalResponse.value.data.data : null
      })
    } catch (err) {
      logger.error('Error fetching child progress:', err)
      setError(err.message || 'Failed to load progress data')
    } finally {
      setLoading(false)
    }
  }, [childId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    loading,
    error,
    refresh: fetchData
  }
}

export default useChildProgress
